import { useQuery } from "@tanstack/react-query"
import { client } from "@/api"

export interface ExceptionGroup {
  issue_id: string
  type: string
  message: string
  occurrences: number
  users: number
  last_seen: string
}

export interface RouteErrorRate {
  route: string
  requests: number
  errors: number
  error_rate: number
}

// Sustained-4xx rows are keyed by (method, route), not route alone — a pattern like
// `/v1/users/me` serves several verbs and pooling them can dilute a broken one below the
// server-side threshold (#1467). Mirrors `RouteClientErrorRate` in the backend.
export interface RouteClientErrorRate extends RouteErrorRate {
  method: string
}

export interface RouteLatency {
  route: string
  requests: number
  p95_ms: number
}

export interface ObservabilityDigest {
  generated_at: string
  environment: string
  window_minutes: number
  cloudflare: {
    ok: boolean
    error?: string
    total_requests: number
    top_errors: RouteErrorRate[]
    // Already threshold-filtered server-side (sustained high 4xx over real volume, #1467).
    top_client_errors: RouteClientErrorRate[]
    // Its own flag: this is a separate AE query, so an empty list means "none" only when true.
    client_errors_ok: boolean
    slowest_routes: RouteLatency[]
  }
  exceptions: {
    ok: boolean
    error?: string
    groups: ExceptionGroup[]
  }
}

// B4: the cross-source observability digest (Cloudflare AE sustained-4xx/5xx/p95/volume +
// PostHog $exception groups). Mirrors the daily Discord digest; window is in minutes
// (default 24h).
export function useObservability(windowMinutes = 1440) {
  return useQuery({
    queryKey: ["admin", "observability", windowMinutes],
    queryFn: async () => {
      const response = await client.admin.observability.digest.$get({
        query: { window: windowMinutes },
      })
      if (!response.ok) {
        throw new Error("Failed to fetch observability digest")
      }
      return response.json() as Promise<ObservabilityDigest>
    },
    refetchInterval: 60000,
  })
}
